/**
 * Página "Mi Perfil" para jugadores
 * 
 * Muestra:
 * - Información personal del futbolista
 * - Datos deportivos de la temporada actual
 * - Estadísticas y métricas
 * - Información de contacto y configuración
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';

const PlayerProfile = () => {
  const { playerData } = useOutletContext();
  const [loading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Variable responsive
  const isMobile = window.innerWidth <= 768;

  // Scroll al top cuando se carga la página
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Memoizar la URL de la imagen para evitar re-renderizados
  const imageUrl = useMemo(() => {
    if (imageError) {
      return '/img/default-player.jpg';
    }
    return `/img/players/${playerData?.futbolista?.foto || 'default-player.jpg'}`;
  }, [playerData?.futbolista?.foto, imageError]);

  // Callback para manejar errores de imagen
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // Generar avatar por defecto
  const generateDefaultAvatar = useCallback((name) => {
    const initials = name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'JJ';
    const colors = ['#FF6B35', '#4352A9', '#2196F3', '#4CAF50', '#9C27B0', '#FF9800'];
    const colorIndex = name ? name.length % colors.length : 0;
    const backgroundColor = colors[colorIndex];

    return (
      <div style={{
        width: isMobile ? '80px' : '120px',
        height: isMobile ? '80px' : '120px',
        borderRadius: '50%',
        backgroundColor: backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: isMobile ? '24px' : '36px',
        fontWeight: 'bold',
        border: '4px solid #4352A9',
        boxShadow: '0 4px 12px rgba(67, 82, 169, 0.3)',
        position: 'relative'
      }}>
        {initials}
        {/* Icono de futbolista */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px'
        }}>
          ⚽
        </div>
      </div>
    );
  }, [isMobile]);

  // Función para obtener abreviatura y color de posición
  const getPositionInfo = useCallback((posicion) => {
    if (!posicion) return null;

    const posicionLower = posicion.toLowerCase();

    if (posicionLower.includes('portero')) {
      return { abbr: 'PO', color: '#FFC107' }; // Amarillo
    } else if (posicionLower.includes('defensa') || posicionLower.includes('lateral')) {
      return { abbr: 'DEF', color: '#2196F3' }; // Azul
    } else if (posicionLower.includes('medio') || posicionLower.includes('extremo')) {
      return { abbr: 'MC', color: '#4CAF50' }; // Verde
    } else if (posicionLower.includes('delantero') || posicionLower.includes('mediapunta')) {
      return { abbr: 'DEL', color: '#F44336' }; // Rojo
    }

    return { abbr: 'POS', color: '#9E9E9E' }; // Gris por defecto
  }, []);

  // Renderizar iconos de posición
  const renderPositionIcons = useCallback((posicionesText) => {
    if (!posicionesText || posicionesText === 'Sin posición asignada') {
      return <span style={{ color: '#999', fontSize: '0.9rem' }}>Sin posición asignada</span>;
    }

    const posiciones = posicionesText.split(' / ').filter(Boolean);

    // Si ambas posiciones son iguales, mostrar solo una
    const posicionesUnicas = posiciones.length === 2 && posiciones[0] === posiciones[1]
      ? [posiciones[0]]
      : posiciones;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {posicionesUnicas.map((posicion, index) => {
          const posInfo = getPositionInfo(posicion);
          if (!posInfo) return null;

          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: posInfo.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                {posInfo.abbr}
              </div>

              {/* Flecha bidireccional entre posiciones diferentes */}
              {index === 0 && posicionesUnicas.length === 2 && (
                <span style={{
                  fontSize: '16px',
                  color: '#666',
                  margin: '0 4px'
                }}>
                  ↔
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [getPositionInfo]);

  // Estilos responsive
  const containerStyle = {
    maxWidth: '100%',
    width: '100%',
    margin: '0 auto',
    padding: isMobile ? '10px' : '20px',
    boxSizing: 'border-box'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: isMobile ? '15px' : '30px',
    marginBottom: isMobile ? '15px' : '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF',
    width: '100%',
    boxSizing: 'border-box'
  };

  const sectionTitleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
    gap: isMobile ? '15px' : '20px',
    width: '100%'
  };

  const infoItemStyle = {
    padding: isMobile ? '12px' : '15px',
    backgroundColor: '#F8F9FF',
    borderRadius: '10px',
    border: '2px solid #E8ECFF',
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0
  };

  const infoLabelStyle = {
    fontSize: '0.9rem',
    color: '#4352A9',
    fontWeight: '600',
    marginBottom: '5px'
  };

  const infoValueStyle = {
    fontSize: window.innerWidth <= 768 ? '1rem' : '1.1rem',
    color: '#061156',
    fontWeight: '500',
    margin: 0
  };

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
    gap: isMobile ? '12px' : '15px',
    width: '100%'
  };

  const statCardStyle = {
    backgroundColor: '#F8F9FF',
    padding: isMobile ? '12px 8px' : '20px',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #E8ECFF',
    width: '100%',
    boxSizing: 'border-box',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const statValueStyle = {
    fontSize: window.innerWidth <= 768 ? '1.8rem' : '2rem',
    fontWeight: '700',
    color: '#4352A9',
    margin: '0 0 5px 0'
  };

  const statLabelStyle = {
    fontSize: '0.9rem',
    color: '#666',
    fontWeight: '500',
    margin: 0
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!playerData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>No se pudieron cargar los datos del perfil.</p>
      </div>
    );
  }

  const futbolista = playerData.futbolista;
  const propoints = playerData.propoints;
  const inscripciones = playerData.inscripciones || [];

  return (
    <div style={containerStyle}>
      {/* Header del Perfil */}
      <div style={cardStyle}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          padding: isMobile ? '15px' : '20px'
        }}>
          {/* Foto del jugador - Globo redondo */}
          <div style={{ position: 'relative' }}>
            {imageError || !futbolista?.foto ? (
              generateDefaultAvatar(futbolista?.nombre)
            ) : (
              <img
                src={imageUrl}
                alt={futbolista?.nombre || 'Jugador'}
                style={{
                  width: isMobile ? '80px' : '120px',
                  height: isMobile ? '80px' : '120px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid #4352A9',
                  boxShadow: '0 4px 12px rgba(67, 82, 169, 0.3)'
                }}
                onError={handleImageError}
                loading="lazy"
              />
            )}

            {/* Círculos con información */}
            <div style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              {/* Potencial Total */}
              <div style={{
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                backgroundColor: '#FF6B35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: isMobile ? '9px' : '10px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid white'
              }}>
                {Math.round(futbolista?.potencial_total || 0)}
              </div>

              {/* Posición Principal */}
              <div style={{
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                backgroundColor: '#2196F3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: isMobile ? '8px' : '9px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid white'
              }}>
                {getPositionInfo(futbolista?.posicion_principal)?.abbr || 'POS'}
              </div>

              {/* Puntos ProPoints */}
              <div style={{
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: isMobile ? '9px' : '10px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid white'
              }}>
                {Math.round(futbolista?.valor_puntos || 0)}
              </div>
            </div>
          </div>

          {/* Información del jugador */}
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: '700',
              color: '#061156',
              margin: '0 0 8px 0'
            }}>
              {futbolista?.nombre}
            </h1>
            {futbolista?.apodo && futbolista.apodo !== futbolista.nombre && (
              <p style={{
                fontSize: '1.1rem',
                color: '#4352A9',
                fontStyle: 'italic',
                margin: '0 0 12px 0'
              }}>
                &ldquo;{futbolista.apodo}&rdquo;
              </p>
            )}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '4px' : '12px',
              fontSize: '0.95rem',
              color: '#666'
            }}>
              <span><strong>{futbolista?.club}</strong></span>
              <span>{futbolista?.division}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas Rápidas */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          <span className="material-icons" style={{ fontSize: '1.5rem', marginRight: '8px' }}>analytics</span>
          Estadísticas
        </h2>
        <div style={statsGridStyle}>
          <div style={statCardStyle}>
            <span className="material-icons" style={{ fontSize: '2rem', color: '#4352A9', marginBottom: '8px' }}>star</span>
            <div style={statValueStyle}>{propoints?.total || 0}</div>
            {!isMobile && <div style={statLabelStyle}>ProPoints Totales</div>}
          </div>
          <div style={statCardStyle}>
            <span className="material-icons" style={{ fontSize: '2rem', color: '#4CAF50', marginBottom: '8px' }}>trending_up</span>
            <div style={statValueStyle}>{propoints?.temporada_actual || 0}</div>
            {!isMobile && <div style={statLabelStyle}>Esta Temporada</div>}
          </div>
          <div style={statCardStyle}>
            <span className="material-icons" style={{ fontSize: '2rem', color: '#FF9800', marginBottom: '8px' }}>leaderboard</span>
            <div style={statValueStyle}>#{propoints?.ranking_temporada || '-'}</div>
            {!isMobile && <div style={statLabelStyle}>Ranking</div>}
          </div>
          <div style={statCardStyle}>
            <span className="material-icons" style={{ fontSize: '2rem', color: '#2196F3', marginBottom: '8px' }}>sports_soccer</span>
            <div style={statValueStyle}>{inscripciones.length}</div>
            {!isMobile && <div style={statLabelStyle}>Inscripciones</div>}
          </div>
        </div>
      </div>

      {/* Información Personal */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          <span className="material-icons" style={{ fontSize: '1.5rem', marginRight: '8px' }}>person</span>
          Información Personal
        </h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Nombre Completo</div>
            <p style={infoValueStyle}>{futbolista?.nombre}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Apodo</div>
            <p style={infoValueStyle}>{futbolista?.apodo || 'Sin apodo'}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Año de Nacimiento</div>
            <p style={infoValueStyle}>{futbolista?.ano_nacimiento}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Edad</div>
            <p style={infoValueStyle}>
              {new Date().getFullYear() - (futbolista?.ano_nacimiento || 0)} años
            </p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>ID de Jugador</div>
            <p style={{
              ...infoValueStyle,
              fontFamily: 'monospace',
              backgroundColor: '#E8ECFF',
              padding: '5px 10px',
              borderRadius: '5px',
              display: 'inline-block'
            }}>
              #{futbolista?.id}
            </p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Estado</div>
            <p style={{
              ...infoValueStyle,
              color: '#4CAF50',
              fontWeight: '600'
            }}>
              ✅ Activo
            </p>
          </div>
        </div>
      </div>

      {/* Información Deportiva */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          <span className="material-icons" style={{ fontSize: '1.5rem', marginRight: '8px' }}>sports_soccer</span>
          Información Deportiva
        </h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Club</div>
            <p style={infoValueStyle}>{futbolista?.club}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>División</div>
            <p style={infoValueStyle}>{futbolista?.division}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Posiciones de Juego</div>
            <div style={{ marginTop: '8px' }}>
              {renderPositionIcons(futbolista?.posicion)}
            </div>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Equipación</div>
            <p style={{
              ...infoValueStyle,
              color: futbolista?.tiene_equipacion ? '#4CAF50' : '#FF9800'
            }}>
              {futbolista?.tiene_equipacion ? '✅ Sí' : '⚠️ Pendiente'}
            </p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Sede por Defecto</div>
            <p style={infoValueStyle}>{futbolista?.sede_por_defecto}</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Inscripciones Activas</div>
            <p style={infoValueStyle}>{inscripciones.length} torneos</p>
          </div>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>🤝 Agente</div>
            {futbolista?.agente ? (
              <div style={{ marginTop: '8px' }}>
                <p style={{
                  ...infoValueStyle,
                  marginBottom: '2px',
                  fontWeight: '600',
                  color: '#4352A9'
                }}>
                  {futbolista.agente.nombre}
                </p>
                {futbolista.agente.rol && (
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    margin: '0 0 8px 0',
                    fontStyle: 'italic'
                  }}>
                    {futbolista.agente.rol}
                  </p>
                )}
                {futbolista.agente.email && (
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#666',
                    margin: '2px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>📧</span>
                    <a
                      href={`mailto:${futbolista.agente.email}`}
                      style={{
                        color: '#4352A9',
                        textDecoration: 'none'
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {futbolista.agente.email}
                    </a>
                  </p>
                )}
                {futbolista.agente.telefono && (
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#666',
                    margin: '2px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>📱</span>
                    <a
                      href={`tel:${futbolista.agente.telefono}`}
                      style={{
                        color: '#4352A9',
                        textDecoration: 'none'
                      }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {futbolista.agente.telefono}
                    </a>
                  </p>
                )}
              </div>
            ) : (
              <p style={{
                ...infoValueStyle,
                color: '#999',
                fontStyle: 'italic'
              }}>
                Sin agente asignado
              </p>
            )}
          </div>
        </div>
      </div>

      {/* RRSS */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📱 RRSS
        </h2>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <div style={infoLabelStyle}>Redes Sociales</div>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {futbolista?.rrss ? (
                <>
                  {/* Instagram */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      📷
                    </div>
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                      @{futbolista.rrss}
                    </span>
                  </div>

                  {/* Facebook */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      backgroundColor: '#1877F2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      f
                    </div>
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                      {futbolista.rrss}
                    </span>
                  </div>
                </>
              ) : (
                <span style={{ color: '#999', fontSize: '0.9rem' }}>
                  No hay redes sociales configuradas
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
